import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  Chip, Tabs, Tab,
} from '@mui/material';
import {
  AccountBalance, AccountBalanceWallet, TrendingUp, TrendingDown,
  SwapHoriz, Receipt, Refresh, CompareArrows, Warning,
  ArrowUpward, ArrowDownward,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;

// ── KPI Card ─────────────────────────────────────────────────────────────────
function Kpi({ icon, label, value, sub, color = '#1565c0', bg = '#e3f2fd', trend, trendLabel }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, borderTop: `3px solid ${color}`, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</Box>
        {trend !== undefined && (
          <Chip
            size="small"
            icon={trend >= 0 ? <ArrowUpward sx={{ fontSize: '12px !important' }} /> : <ArrowDownward sx={{ fontSize: '12px !important' }} />}
            label={trendLabel || `${Math.abs(trend).toFixed(1)}%`}
            sx={{ fontSize: 10, height: 20, bgcolor: trend >= 0 ? '#e8f5e9' : '#fce4ec', color: trend >= 0 ? '#2e7d32' : '#b71c1c' }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color, mt: 0.25, lineHeight: 1.2 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>{sub}</Typography>}
    </Paper>
  );
}

// ── Banco Card ────────────────────────────────────────────────────────────────
function BancoCard({ banco }) {
  const activo = banco.activa !== false;
  return (
    <Paper sx={{
      p: 2, borderRadius: 2, border: '1px solid', borderColor: activo ? '#b2dfdb' : '#e0e0e0',
      bgcolor: activo ? '#f1f8f7' : '#fafafa', position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: activo ? '#00897b' : '#9e9e9e' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#004d40' }}>{banco.nombre_banco}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{banco.tipo_cuenta}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
            {banco.numero_cuenta ? `****${String(banco.numero_cuenta).slice(-4)}` : '—'}
          </Typography>
        </Box>
        <AccountBalance sx={{ color: activo ? '#00897b' : '#9e9e9e', fontSize: 32 }} />
      </Box>
      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: activo ? '#b2dfdb' : '#e0e0e0' }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Saldo actual</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: activo ? '#00695c' : '#757575' }}>{fmt(banco.saldo_actual)}</Typography>
      </Box>
      {!activo && <Chip label="Inactiva" size="small" sx={{ mt: 1, fontSize: 10, height: 18, bgcolor: '#eeeeee', color: '#757575' }} />}
    </Paper>
  );
}

// ── Caja Card ─────────────────────────────────────────────────────────────────
function CajaCard({ caja }) {
  const activa = caja.activa !== false;
  return (
    <Paper sx={{
      p: 2, borderRadius: 2, border: '1px solid', borderColor: activa ? '#c8e6c9' : '#e0e0e0',
      bgcolor: activa ? '#f1f8f0' : '#fafafa', position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: activa ? '#43a047' : '#9e9e9e' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1b5e20' }}>{caja.nombre}</Typography>
          {caja.descripcion && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{caja.descripcion}</Typography>}
        </Box>
        <AccountBalanceWallet sx={{ color: activa ? '#43a047' : '#9e9e9e', fontSize: 32 }} />
      </Box>
      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: activa ? '#c8e6c9' : '#e0e0e0' }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Saldo actual</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: activa ? '#2e7d32' : '#757575' }}>{fmt(caja.saldo_actual)}</Typography>
      </Box>
    </Paper>
  );
}

// ── Tooltip personalizado para el chart ──────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, fontSize: 12 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: 12 }}>{label}</Typography>
      {payload.map((p) => (
        <Box key={p.name} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.fill }} />
          <Typography sx={{ fontSize: 11 }}>{p.name}: {fmt(p.value)}</Typography>
        </Box>
      ))}
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Tesoreria() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [flujoData, setFlujoData] = useState([]);
  const [cuentasTab, setCuentasTab] = useState(0); // 0=Bancos 1=Cajas

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Rango mes actual + 3 meses atrás para el chart
      const hoy = new Date();
      const inicioChart = new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1).toISOString().slice(0, 10);
      const finChart = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10);

      const [resRes, bancosRes, cajasRes] = await Promise.all([
        axios.get(`${API}/api/tesoreria/resumen`, { headers }),
        axios.get(`${API}/api/tesoreria/bancos`, { headers }),
        axios.get(`${API}/api/tesoreria/cajas`, { headers }),
      ]);

      setResumen(resRes.data.data);
      setBancos(bancosRes.data.data || []);
      setCajas(cajasRes.data.data || []);

      // Construir datos de gráfica: flujo por mes (últimos 3 meses)
      const meses = [];
      for (let i = 2; i >= 0; i--) {
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
        const fin = new Date(hoy.getFullYear(), hoy.getMonth() - i + 1, 0);
        const label = inicio.toLocaleDateString('es-EC', { month: 'short', year: '2-digit' });
        try {
          const r = await axios.get(
            `${API}/api/tesoreria/flujo-caja?fechaInicio=${inicio.toISOString().slice(0, 10)}&fechaFin=${fin.toISOString().slice(0, 10)}`,
            { headers }
          );
          meses.push({ mes: label, Ingresos: r.data.data?.ingresos || 0, Egresos: r.data.data?.egresos || 0, 'Flujo Neto': r.data.data?.flujo_neto || 0 });
        } catch {
          meses.push({ mes: label, Ingresos: 0, Egresos: 0, 'Flujo Neto': 0 });
        }
      }
      setFlujoData(meses);
    } catch (err) {
      console.error(err);
      setError('Error al cargar datos de tesorería');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: '#00897b' }} />
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Cargando Tesorería...</Typography>
      </Box>
    );
  }

  const flujoNeto = resumen?.flujo_mes || 0;
  const bancosActivos = bancos.filter(b => b.activa !== false);
  const cajasActivas = cajas.filter(c => c.activa !== false);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#004d40', letterSpacing: -0.5 }}>
            🏦 Tesorería
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
            Control de caja, bancos y flujo de fondos
          </Typography>
        </Box>
        <Button
          size="small" startIcon={<Refresh />}
          onClick={cargarTodo}
          sx={{ textTransform: 'none', color: '#00897b', borderColor: '#b2dfdb', border: '1px solid' }}
        >
          Actualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── KPIs ───────────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
        gap: 2, mb: 3,
      }}>
        <Kpi
          icon={<AccountBalance />}
          label="Saldo Total"
          value={fmt(resumen?.saldo_total)}
          sub={`${bancosActivos.length + cajasActivas.length} cuentas activas`}
          color="#1565c0" bg="#e3f2fd"
        />
        <Kpi
          icon={<AccountBalance />}
          label="Saldo Bancos"
          value={fmt(resumen?.saldo_bancos)}
          sub={`${bancosActivos.length} banco(s)`}
          color="#00695c" bg="#e0f2f1"
        />
        <Kpi
          icon={<AccountBalanceWallet />}
          label="Saldo Cajas"
          value={fmt(resumen?.saldo_cajas)}
          sub={`${cajasActivas.length} caja(s)`}
          color="#2e7d32" bg="#e8f5e9"
        />
        <Kpi
          icon={<TrendingUp />}
          label="Ingresos del Mes"
          value={fmt(resumen?.ingresos_mes)}
          sub="Mes actual"
          color="#1b5e20" bg="#f1f8e9"
        />
        <Kpi
          icon={<TrendingDown />}
          label="Egresos del Mes"
          value={fmt(resumen?.egresos_mes)}
          sub="Mes actual"
          color="#b71c1c" bg="#fce4ec"
        />
      </Box>

      {/* ── Flujo Neto Banner + Chart ────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '300px 1fr' }, gap: 2, mb: 3 }}>

        {/* Flujo neto card */}
        <Paper sx={{
          p: 3, borderRadius: 2,
          bgcolor: flujoNeto >= 0 ? '#e0f2f1' : '#fce4ec',
          border: `2px solid ${flujoNeto >= 0 ? '#00897b' : '#e53935'}`,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHoriz sx={{ color: flujoNeto >= 0 ? '#00695c' : '#b71c1c', fontSize: 32 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Flujo Neto del Mes
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 36, fontWeight: 900, color: flujoNeto >= 0 ? '#00695c' : '#b71c1c', lineHeight: 1 }}>
            {fmt(flujoNeto)}
          </Typography>
          <Chip
            label={flujoNeto >= 0 ? 'Positivo ▲' : 'Negativo ▼'}
            size="small"
            sx={{
              fontWeight: 700, fontSize: 11, width: 'fit-content',
              bgcolor: flujoNeto >= 0 ? '#00897b' : '#e53935',
              color: 'white',
            }}
          />
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Ingresos</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#2e7d32' }}>{fmt(resumen?.ingresos_mes)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Egresos</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#b71c1c' }}>{fmt(resumen?.egresos_mes)}</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Gráfica últimos 3 meses */}
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 2, color: 'text.primary' }}>
            📊 Flujo Mensual — Últimos 3 meses
          </Typography>
          {flujoData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={flujoData} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <RTooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Ingresos" fill="#00897b" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Egresos" fill="#ef5350" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Flujo Neto" fill="#1565c0" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Sin transacciones en el período</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Cheques Pendientes Alert ─────────────────────────────────────────── */}
      {resumen?.cheques_pendientes > 0 && (
        <Paper sx={{
          p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff8e1',
          border: '1px solid #ffc107', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#f57f17', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#e65100' }}>
                {resumen.cheques_pendientes} cheque(s) pendiente(s) de cobro
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Monto total: <strong>{fmt(resumen.monto_cheques_pendientes)}</strong>
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained" size="small" startIcon={<Receipt />}
            onClick={() => navigate('/tesoreria/cheques')}
            sx={{ bgcolor: '#f57f17', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none' }}
          >
            Ver Cheques
          </Button>
        </Paper>
      )}

      {/* ── Cuentas: Bancos / Cajas ──────────────────────────────────────────── */}
      <Paper sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Cuentas</Typography>
          <Tabs
            value={cuentasTab} onChange={(_, v) => setCuentasTab(v)}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, textTransform: 'none', py: 0 } }}
          >
            <Tab label={`Bancos (${bancosActivos.length})`} />
            <Tab label={`Cajas (${cajasActivas.length})`} />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {cuentasTab === 0 && (
            bancosActivos.length === 0
              ? <Typography sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center', py: 3 }}>Sin cuentas bancarias activas</Typography>
              : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2 }}>
                  {bancosActivos.map(b => <BancoCard key={b.id} banco={b} />)}
                </Box>
              )
          )}
          {cuentasTab === 1 && (
            cajasActivas.length === 0
              ? <Typography sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center', py: 3 }}>Sin cajas activas</Typography>
              : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2 }}>
                  {cajasActivas.map(c => <CajaCard key={c.id} caja={c} />)}
                </Box>
              )
          )}
        </Box>
      </Paper>

      {/* ── Navegación rápida ────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
          Módulos de Tesorería
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
          gap: 1.5,
        }}>
          {[
            { label: 'Cajas', icon: '🗃️', path: '/tesoreria/cajas', color: '#2e7d32', bg: '#e8f5e9' },
            { label: 'Bancos', icon: '🏦', path: '/tesoreria/bancos', color: '#00695c', bg: '#e0f2f1' },
            { label: 'Transacciones', icon: '↔️', path: '/tesoreria/transacciones', color: '#1565c0', bg: '#e3f2fd' },
            { label: 'Flujo de Caja', icon: '📈', path: '/tesoreria/flujo-caja', color: '#6a1b9a', bg: '#f3e5f5' },
            { label: 'Cheques', icon: '📋', path: '/tesoreria/cheques', color: '#e65100', bg: '#fff3e0' },
            { label: 'Conciliación', icon: '⚖️', path: '/tesoreria/conciliacion', color: '#ad1457', bg: '#fce4ec' },
          ].map(({ label, icon, path, color, bg }) => (
            <Button
              key={path}
              fullWidth
              onClick={() => navigate(path)}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 0.5,
                py: 1.5, borderRadius: 2, textTransform: 'none',
                bgcolor: bg, color, border: `1px solid ${color}22`,
                '&:hover': { bgcolor: bg, opacity: 0.85, borderColor: color },
                fontSize: 12, fontWeight: 600,
              }}
            >
              <span style={{ fontSize: 22 }}>{icon}</span>
              {label}
            </Button>
          ))}
        </Box>
      </Paper>

    </Box>
  );
}
